import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  getWireInstructions,
  getWireInstruction,
  addWireInstruction,
  editWireInstruction,
  deleteWireInstructions,
  activeWireInstruction,
  inactiveWireInstruction,
  startLoadingWireInstructions,
  stopLoadingWireInstructions,
} from 'app/state/ducks/wire-instructions/actions'
import { WireInstructionParams } from 'app/models/wire-instruction-params'
import {
  coalesceThunk,
  coalesceThunkByKey,
} from 'app/state/utils/coalesce-thunk'
import { LOAD_FAILED, failed } from 'app/state/utils/load-result'

export const saveWireInstruction =
  (
    savedWireInstruction: WireInstructionParams,
    wireInstruction?: WireInstructionParams,
  ) =>
  async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = savedWireInstruction.status.toLowerCase() || 'inactive'

    const updated_savedWI: WireInstructionParams = {
      ...savedWireInstruction,
      hasError: false,
      hasSuccessfulEntitySave: false,
      ErrorMessage: '',
      customer_token: config.company.toUpperCase(),
    }

    if (wireInstruction) {
      return client
        .put(
          `${apiServerUrl}/api/${statusLower}-wire-instructions`,
          updated_savedWI,
        )
        .then(wireInstruction => {
          wireInstruction.data.status = savedWireInstruction.status
          const payload = dispatch(editWireInstruction(wireInstruction))
          return { success: true, payload: payload.payload }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }
    delete savedWireInstruction.id
    return client
      .post(
        `${apiServerUrl}/api/${statusLower}-wire-instructions`,
        savedWireInstruction,
      )
      .then(wireInstruction => {
        wireInstruction.data.status = statusLower
        const payload = dispatch(addWireInstruction(wireInstruction))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

const loadInactiveWireInstructions = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/inactive-wire-instructions`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

const loadActiveWireInstructions = async () => {
  const apiServerUrl = config.backendUrl
  return await client
    .get(`${apiServerUrl}/api/active-wire-instructions`)
    .then(response => {
      return response.data
    })
    .catch(e => {
      console.log(e)
      return null
    })
}

export const loadWireInstructions = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    try {
      dispatch(startLoadingWireInstructions())
      const [inactiveWireInstructions, activeWireInstructions] =
        await Promise.all([
          loadInactiveWireInstructions(),
          loadActiveWireInstructions(),
        ])
      if ([inactiveWireInstructions, activeWireInstructions].some(failed)) return LOAD_FAILED
      const allWireInstructions = inactiveWireInstructions
        .map((wireInstruction: WireInstructionParams) => ({
          ...wireInstruction,
          status: 'Inactive',
        }))
        .concat(
          activeWireInstructions.map(
            (wireInstruction: WireInstructionParams) => ({
              ...wireInstruction,
              status: 'Active',
            }),
          ),
        )
      return dispatch(getWireInstructions(allWireInstructions))
    } catch (e) {
      console.log(e)
      return LOAD_FAILED
    } finally {
      dispatch(stopLoadingWireInstructions())
    }
  },
)

export const loadOneWireInstruction = coalesceThunkByKey(
  (wireInstructionId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = status.toLowerCase()
    return client
      .get(
        `${apiServerUrl}/api/${statusLower}-wire-instructions/one?id=${wireInstructionId}`,
      )
      .then(wireInstruction => {
        return dispatch(
          getWireInstruction({ ...wireInstruction.data, status: status }),
        )
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const removeWireInstructions =
  (wireInstruction: (string | undefined)[]) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .delete(
        `${apiServerUrl}/api/inactive-wire-instructions?ids[]=${wireInstruction}`,
      )
      .then(wireInstructions => {
        return dispatch(deleteWireInstructions(wireInstructions.data))
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const activateWireInstruction =
  (wireInstruction: WireInstructionParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/inactive-wire-instructions/activate`, {
        id: wireInstruction.id,
      })
      .then(response => {
        const payload = dispatch(activeWireInstruction(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const deactivateWireInstruction =
  (wireInstruction: WireInstructionParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/active-wire-instructions/deactivate`, {
        id: wireInstruction.id,
      })
      .then(response => {
        const payload = dispatch(inactiveWireInstruction(response.data))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }
